import React, { useState, useRef, useEffect } from 'react';
import { PartAttribute } from '@/app/types/cad';
import { X } from 'lucide-react';

interface TextDialogProps {
  isOpen: boolean;
  initialText?: string;
  initialRotation?: number; // in radians
  attributes: PartAttribute[];
  onConfirm: (text: string, rotation: number) => void;
  onCancel: () => void;
}

export function TextDialog({ 
  isOpen, 
  initialText = '', 
  initialRotation = 0, 
  attributes, 
  onConfirm, 
  onCancel 
}: TextDialogProps) {
  const [text, setText] = useState(initialText);
  const [rotation, setRotation] = useState(initialRotation);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setText(initialText);
      setRotation(initialRotation);
      // Focus textarea after a brief delay to ensure dialog is rendered
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.select();
      }, 50);
    }
  }, [isOpen, initialText, initialRotation]);

  if (!isOpen) return null;

  const handleInsertAttribute = (attrName: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = text.substring(0, start);
    const after = text.substring(end);
    const placeholder = `{${attrName}}`;
    
    const newText = before + placeholder + after;
    setText(newText);
    
    // Set cursor position after inserted placeholder
    setTimeout(() => {
      textarea.focus();
      const newPos = start + placeholder.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 10);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim()) {
      onConfirm(text, rotation);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      if (text.trim()) {
        onConfirm(text, rotation);
      }
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onCancel}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-[500px] max-w-[90vw]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: 'var(--md-outline-variant)' }}
        >
          <h3 className="font-medium" style={{ color: 'var(--md-on-surface)' }}>
            Text eingeben
          </h3>
          <button
            onClick={onCancel}
            className="p-1 rounded hover:bg-black/5 transition-colors"
          >
            <X className="w-5 h-5" style={{ color: 'var(--md-on-surface-variant)' }} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Text input */}
            <div>
              <label 
                className="block text-sm mb-2 font-medium" 
                style={{ color: 'var(--md-on-surface)' }}
              >
                Text
              </label>
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border rounded-md resize-none font-mono"
                style={{ 
                  borderColor: 'var(--md-outline)',
                  minHeight: '100px'
                }}
                placeholder="Text eingeben..."
              />
              <p className="text-xs mt-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                Verwende {'{AttributName}'} um Attribute einzufügen. 
                <span className="font-medium"> Strg+Enter</span> zum Speichern
              </p>
            </div>

            {/* Rotation/Angle */}
            <div>
              <label 
                className="block text-sm mb-2 font-medium" 
                style={{ color: 'var(--md-on-surface)' }}
              >
                Winkel
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: '0°', value: 0 },
                  { label: '30°', value: 30 },
                  { label: '45°', value: 45 },
                  { label: '60°', value: 60 },
                  { label: '90°', value: 90 },
                  { label: '120°', value: 120 },
                  { label: '150°', value: 150 },
                  { label: '180°', value: 180 },
                  { label: '210°', value: 210 },
                  { label: '240°', value: 240 },
                  { label: '270°', value: 270 },
                  { label: '330°', value: 330 },
                ].map(({ label, value }) => {
                  const radians = (value * Math.PI) / 180;
                  const isSelected = Math.abs(rotation - radians) < 0.01;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setRotation(radians)}
                      className="px-3 py-2 rounded text-sm border transition-colors"
                      style={{
                        borderColor: isSelected ? 'var(--md-primary)' : 'var(--md-outline-variant)',
                        backgroundColor: isSelected ? 'var(--md-primary-container)' : 'white',
                        color: isSelected ? 'var(--md-on-primary-container)' : 'var(--md-on-surface)',
                        fontWeight: isSelected ? 600 : 400,
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <label className="text-xs" style={{ color: 'var(--md-on-surface-variant)' }}>
                  Oder manuell:
                </label>
                <input
                  type="number"
                  min="0"
                  max="360"
                  step="1"
                  value={Math.round((rotation * 180) / Math.PI)}
                  onChange={(e) => {
                    const degrees = parseFloat(e.target.value) || 0;
                    setRotation((degrees * Math.PI) / 180);
                  }}
                  className="flex-1 px-2 py-1 text-sm border rounded"
                  style={{ borderColor: 'var(--md-outline-variant)' }}
                />
                <span className="text-xs" style={{ color: 'var(--md-on-surface-variant)' }}>°</span>
              </div>
            </div>

            {/* Attributes */}
            {attributes.length > 0 && (
              <div>
                <label 
                  className="block text-sm mb-2 font-medium" 
                  style={{ color: 'var(--md-on-surface)' }}
                >
                  Attribute einfügen
                </label>
                <div className="flex flex-wrap gap-2">
                  {attributes.map((attr) => (
                    <button
                      key={attr.id}
                      type="button"
                      onClick={() => handleInsertAttribute(attr.name)}
                      className="px-2 py-1 rounded text-xs border hover:bg-black/5 transition-colors"
                      style={{
                        borderColor: 'var(--md-outline-variant)',
                        color: 'var(--md-on-surface)',
                      }}
                      title={`Fügt {${attr.name}} ein`}
                    >
                      <span className="font-mono">{'{' + attr.name + '}'}</span>
                      {attr.value && (
                        <span 
                          className="ml-1 opacity-60"
                          style={{ color: 'var(--md-on-surface-variant)' }}
                        >
                          = {attr.value.toString()}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Preview */}
            {text && (
              <div>
                <label 
                  className="block text-sm mb-2 font-medium" 
                  style={{ color: 'var(--md-on-surface)' }}
                >
                  Vorschau
                </label>
                <div 
                  className="px-3 py-2 rounded border"
                  style={{
                    borderColor: 'var(--md-outline-variant)',
                    backgroundColor: 'var(--md-surface-container-low)',
                  }}
                >
                  <p className="text-sm" style={{ color: 'var(--md-on-surface)' }}>
                    {resolveAttributePlaceholders(text, attributes)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div 
            className="flex justify-end gap-2 px-4 py-3 border-t"
            style={{ 
              borderColor: 'var(--md-outline-variant)',
              backgroundColor: 'var(--md-surface-container-low)'
            }}
          >
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded text-sm hover:bg-black/5 transition-colors"
              style={{ color: 'var(--md-on-surface)' }}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={!text.trim()}
              className="px-4 py-2 rounded text-sm text-white transition-colors disabled:opacity-50"
              style={{ 
                backgroundColor: 'var(--md-primary)',
                opacity: text.trim() ? 1 : 0.5
              }}
            >
              Einfügen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Helper function to resolve attribute placeholders in text
export function resolveAttributePlaceholders(text: string, attributes: PartAttribute[]): string {
  let resolvedText = text;
  
  // Match all {AttributeName} patterns
  const placeholderRegex = /\{([^}]+)\}/g;
  const matches = text.matchAll(placeholderRegex);
  
  for (const match of matches) {
    const placeholder = match[0]; // e.g., "{DN}"
    const attrName = match[1]; // e.g., "DN"
    
    // Find matching attribute (case-insensitive)
    const attr = attributes.find(a => a.name.toLowerCase() === attrName.toLowerCase());
    
    if (attr) {
      // Replace with actual value
      const value = attr.value?.toString() || '';
      resolvedText = resolvedText.replace(placeholder, value);
    }
    // If attribute not found, leave placeholder as-is
  }
  
  return resolvedText;
}